import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import useRestorableState from "hooks/useRestorableState";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Page,
  Banner,
  Tabs,
  TabsProps,
  Button as PolarisButton,
  Card,
  TextField,
  Select,
} from "@shopify/polaris";
import { Badge } from "catalyst-ui-kit/badge";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import PaginationControls from "components/pagination/PaginationControls";
import WorkSecurityAssignmentsPanel from "containers/work-security-assignments-container/work-security-assignments-panel";
import WorkSecurityIncidentsPanel from "containers/work-security-incidents-container/work-security-incidents-panel";
import WorkSecuritySignedDocumentsPanel from "containers/work-security-signed-documents-container/work-security-signed-documents-panel";
import WorkSecurityArchivedPanel from "containers/work-security-archived-container/work-security-archived-panel";
import WorkSecurityTrainingPanel from "containers/work-security-training-container/work-security-training-panel";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchWorkSecurityRoles,
  fetchWorkSecurityGroups,
  WorkSecurityGroup,
  WorkSecurityListPagination,
  WorkSecurityRole,
} from "services/work-security.service";
import { useAreaScope } from "features/work-security-areas/useAreaScope";
import withAreaQuery from "features/work-security-areas/with-area-query";
import { useCabinetSummary } from "features/cabinet-tasks/use-cabinet-summary";
import WsApprovalsTab from "features/work-security-approvals/WsApprovalsTab";
import { PositionsList } from "features/positions/PositionsPage";
import { personalasTabsFor } from "features/personnel/personnel-helpers";
import { useAppSelector } from "hooks";
import { Column } from "types/Datatable";
import { PHONE_PAGE_GUTTER, PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import useIsPhone from "features/files/use-is-phone";
import useMediaQuery from "./use-media-query";

/**
 * Descriptor-driven tab list. Replaces the old includes/endsWith chain on
 * `pathname` (see git history) with an explicit, filterable list — needed
 * because the "Įvykiai" tab is now conditionally visible per area (see
 * `showIncidentsTab` below). No sphere segment is added to the path: the
 * area lives only in the `?area` query param (owner decision — the module's
 * URL shape does not change).
 */
type WorkSecurityTabDescriptor = {
  key: "roles" | "approvals" | "incidents" | "assignments" | "signed-documents" | "archive" | "positions" | "training";
  /** Suffix appended to `/work-security`; "" for the roles tab itself. */
  path: string;
  labelKey: string;
  defaultLabel: string;
  panelId: string;
  matches: (pathname: string) => boolean;
  visible: boolean;
  /** Count shown next to the label (only when > 0). */
  badge?: number;
};

const MINOR_COLUMN_KEYS = ["#", "description", "createdAt"];

/** Tailwind's `xl` (1280 px) breakpoint as `useMediaQuery` asks for it. */
export const BELOW_XL_QUERY = "(max-width: 1279.98px)";

/** The eight tabs scroll sideways on a phone with nothing hinting at the hidden ones, so there they are one picker. */
function SectionPicker({
  tabs,
  selected,
  onSelect,
  children,
}: Pick<TabsProps, "tabs" | "selected" | "onSelect" | "children">) {
  const { t } = useTranslation();
  return (
    <div>
      <div className={PHONE_PAGE_GUTTER}>
        <Select
          label={t("ws_section_picker", { defaultValue: "Section" })}
          labelHidden
          options={tabs.map((tab, index) => ({
            label: tab.badge ? `${tab.content} (${tab.badge})` : tab.content,
            value: String(index),
          }))}
          value={String(selected)}
          onChange={(value) => onSelect?.(Number(value))}
        />
      </div>
      {children}
    </div>
  );
}

export default function WorkSecurityRoles() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [roles, setRoles] = useState<WorkSecurityRole[]>([]);
  const [pagination, setPagination] = useState<WorkSecurityListPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [areaWarningDismissed, setAreaWarningDismissed] = useState(false);

  const scope = useAreaScope(companyId);
  const role = useAppSelector((state) => state.authReducer.role);
  const isPhone = useIsPhone();
  // Below xl the roles table drops its low-value columns so a portrait tablet needs no sideways scroll.
  const isBelowXl = useMediaQuery(BELOW_XL_QUERY);

  // «Laukia patvirtinimo» badge — the cabinet summary's count of procedure approvals (the
  // tab itself and every approval ask the summary again).
  const approvalsBadge = useCabinetSummary(companyId).summary?.queues.ws?.count ?? 0;

  // "All areas" (scope.areaId === "") counts every accessible area with
  // hasIncidents; a concrete selection only counts that one. While /areas is
  // still loading (or failed — degrade to "all accessible") there is no
  // per-area data yet, so default to visible: that matches today's behaviour
  // (the tab is always shown) for the overwhelmingly common single-system-area
  // company.
  const incidentsRelevantAreas =
    scope.areaId && scope.selectedArea ? [scope.selectedArea] : scope.areas;
  const showIncidentsTab =
    scope.areas.length === 0 || incidentsRelevantAreas.some((area) => area.hasIncidents);

  const tabDescriptors: WorkSecurityTabDescriptor[] = [
    {
      key: "roles",
      path: "",
      labelKey: "work_security_roles_tab",
      defaultLabel: "Roles",
      panelId: "ws-roles-panel",
      matches: () => true, // fallback — checked last, see selectedTab below
      visible: true,
    },
    {
      key: "approvals",
      path: "approvals",
      labelKey: "ws_approvals_tab",
      defaultLabel: "Awaiting approval",
      panelId: "ws-approvals-panel",
      matches: (pathname) => pathname.endsWith("/work-security/approvals"),
      visible: true,
      badge: approvalsBadge,
    },
    {
      key: "incidents",
      path: "incidents",
      labelKey: "incidents",
      defaultLabel: "Incidents",
      panelId: "ws-incidents-panel",
      matches: (pathname) => pathname.endsWith("/incidents"),
      visible: showIncidentsTab,
    },
    {
      key: "assignments",
      path: "assignments",
      labelKey: "role_assignments_dashboard",
      defaultLabel: "Role Assignments",
      panelId: "ws-assignments-panel",
      matches: (pathname) => pathname.endsWith("/assignments"),
      visible: true,
    },
    {
      key: "signed-documents",
      path: "signed-documents",
      labelKey: "signed_documents",
      defaultLabel: "Signed Documents",
      panelId: "ws-signed-documents-panel",
      matches: (pathname) => pathname.endsWith("/signed-documents"),
      visible: true,
    },
    {
      key: "archive",
      path: "archive",
      labelKey: "archived",
      defaultLabel: "Archive",
      panelId: "ws-archive-panel",
      matches: (pathname) => pathname.endsWith("/archive"),
      visible: true,
    },
    {
      key: "positions",
      path: "positions",
      labelKey: "positions_title",
      defaultLabel: "Positions",
      panelId: "ws-positions-panel",
      matches: (pathname) => pathname.endsWith("/work-security/positions"),
      // Same rule as the Personalas «Pareigos» tab; the list hides the editing for non-HR.
      visible: personalasTabsFor(role).positions,
    },
    {
      key: "training",
      path: "training",
      labelKey: "training",
      defaultLabel: "Training",
      panelId: "ws-training-panel",
      matches: (pathname) => pathname.includes("/work-security/training"),
      visible: true,
    },
  ];
  const visibleTabDescriptors = tabDescriptors.filter((tab) => tab.visible);

  // The active tab is driven by the URL (so it's bookmarkable and the URL
  // shows which tab you're on): /work-security = Roles, /work-security/assignments
  // = Role Assignments, /work-security/signed-documents = Signed Documents.
  // Each tab is a real route declared in App.tsx. "roles" always matches last
  // (it's the fallback when nothing more specific does).
  const matchedDescriptor =
    visibleTabDescriptors.find(
      (tab) => tab.key !== "roles" && tab.matches(location.pathname)
    ) || visibleTabDescriptors.find((tab) => tab.key === "roles");
  const selectedTab = Math.max(
    matchedDescriptor ? visibleTabDescriptors.indexOf(matchedDescriptor) : 0,
    0
  );
  const activeTabKey = visibleTabDescriptors[selectedTab]?.key || "roles";


  // Internal navigation keeps the current ?area — a Master mid-review of
  // "Kibernetinis saugumas" switching tabs must not silently fall back to
  // "all areas" or the system sphere.
  const withArea = useCallback(
    (path: string) => withAreaQuery(path, new URLSearchParams(location.search).get("area")),
    [location.search]
  );
  // Switching to a sphere that keeps no incident register hides that tab, and
  // the roles list takes over — but the path would still read /incidents, so a
  // refresh or a shared link would show one thing and claim another.
  useEffect(() => {
    if (!companyId) return;
    if (matchedDescriptor) return;
    navigate(withArea(`/app/${companyId}/work-security`), { replace: true });
  }, [companyId, matchedDescriptor, navigate, withArea]);

  const handleTabSelect = (index: number) => {
    const descriptor = visibleTabDescriptors[index];
    if (!descriptor) return;
    const suffix = descriptor.path ? `/${descriptor.path}` : "";
    navigate(withArea(`/app/${companyId}/work-security${suffix}`));
  };

  // Remembered across navigation so Back returns to the same page of results.
  const [paginationState, setPaginationState] = useRestorableState(
    "work-security-pagination",
    { page: 1, pageSize: 20 }
  );
  const [textSearch, setTextSearch] = useRestorableState(
    "work-security-search",
    ""
  );
  const [debouncedTextSearch, setDebouncedTextSearch] = useRestorableState(
    "work-security-applied-search",
    ""
  );
  const [assignmentFilter, setAssignmentFilter] = useRestorableState(
    "work-security-assignment",
    ""
  );
  const [groupFilter, setGroupFilter] = useRestorableState(
    "work-security-group",
    ""
  );
  const [groups, setGroups] = useState<WorkSecurityGroup[]>([]);
  const rolesRequestRef = useRef(0);

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];
  const hasActiveFilters = Boolean(
    debouncedTextSearch.trim() || assignmentFilter || groupFilter
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTextSearch((current) => {
        if (current === textSearch) return current;
        setPaginationState((prev) => ({ ...prev, page: 1 }));
        return textSearch;
      });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [textSearch, setDebouncedTextSearch, setPaginationState]);

  // Switching company or sphere clears the filters, but arriving on the page
  // must not: the remembered ones are exactly what Back is there to bring back.
  const filteredScopeRef = useRef(`${companyId}|${scope.areaId ?? ""}`);
  useEffect(() => {
    const scopeKey = `${companyId}|${scope.areaId ?? ""}`;
    if (filteredScopeRef.current === scopeKey) return;
    filteredScopeRef.current = scopeKey;
    setPaginationState({ page: 1, pageSize: 20 });
    setTextSearch("");
    setDebouncedTextSearch("");
    setAssignmentFilter("");
    setGroupFilter("");
    setRoles([]);
    setPagination(null);
  }, [
    companyId,
    scope.areaId,
    setPaginationState,
    setTextSearch,
    setDebouncedTextSearch,
    setAssignmentFilter,
    setGroupFilter,
  ]);

  const loadGroups = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await fetchWorkSecurityGroups(companyId, {
        ...(scope.areaId ? { areaId: scope.areaId } : {}),
      });
      setGroups(data.groups || []);
    } catch (err) {
      console.error("Failed to load work security groups", err);
    }
  }, [companyId, scope.areaId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadRoles = useCallback(async () => {
    const requestId = ++rolesRequestRef.current;
    if (!companyId) {
      setLoading(false);
      return;
    }
    // See useAreaScope's `resolved` doc: blocks only while a `?area=` from
    // the URL is still unvalidated, so a foreign/stale link can't flash
    // another sphere's roles before the 403 lands (keep showing the loading
    // skeleton, not an empty state, while that resolves). Everywhere else
    // this is already true, so this fetch fires in parallel with /areas,
    // same as before areas existed.
    if (!scope.resolved) {
      return;
    }
    try {
      setLoading(true);
      const data = await fetchWorkSecurityRoles(companyId, {
        page,
        pageSize,
        textSearch: debouncedTextSearch,
        assignmentFilter: assignmentFilter as "" | "with_users" | "without_users",
        groupFilter,
        ...(scope.areaId ? { areaId: scope.areaId } : {}),
      });
      if (requestId !== rolesRequestRef.current) return;

      setLoadError(false);
      setRoles(data.entries || []);
      setPagination(data.pagination || null);
    } catch (err) {
      if (requestId !== rolesRequestRef.current) return;
      console.error("Failed to load work security roles", err);
      setLoadError(true);
    } finally {
      if (requestId === rolesRequestRef.current) {
        setLoading(false);
      }
    }
  }, [
    companyId,
    page,
    pageSize,
    debouncedTextSearch,
    assignmentFilter,
    groupFilter,
    scope.resolved,
    scope.areaId,
  ]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleCreateRoleClick = () => {
    dispatch(
      setModalxOptions({
        title: t("create_work_security_role", { defaultValue: "Create Role" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityRoleFormModal"));
    dispatch(
      setModalxComponentProps({
        // PR-2 §P4: lets the form show a read-only "Sritis: <name>" plaque
        // when scoped to one sphere, or a required Select in "all spheres" mode.
        areas: scope.areas,
        selectedArea: scope.selectedArea,
        onSuccess: () => {
          setPaginationState((prev) => ({ ...prev, page: 1 }));
          loadRoles();
        },
      })
    );
  };

  const handleManageGroupsClick = () => {
    dispatch(
      setModalxOptions({
        title: t("manage_groups", { defaultValue: "Manage Groups" }),
      })
    );
    dispatch(setModalxComponent("WorkSecurityGroupsModal"));
    dispatch(
      setModalxComponentProps({
        areas: scope.areas,
        selectedArea: scope.selectedArea,
        onChanged: () => {
          loadGroups();
          loadRoles();
        },
      })
    );
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState({ page: 1, pageSize: nextPageSize });
    }
  };

  const rolesCollection = roles.map((role) => ({
    ...role,
    viewUrl: withArea(`/app/${companyId}/work-security/${role.id}`),
  }));

  // The table shows from 768 px up (a phone gets cards). Between 768 and 1280 px it has no room for
  // every column, so it leaves out the ones nobody needs to pick a row.
  const withoutMinorColumns = (columns: Column[]) =>
    isBelowXl && !isPhone
      ? columns.filter((column) => !MINOR_COLUMN_KEYS.includes(column.key))
      : columns;

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : roles.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
        ? Math.ceil(totalItems / pageSize)
        : 0;
  const hasNextPage = totalPages > 0 ? page < totalPages : false;
  const hasPreviousPage = page > 1;
  const shouldShowPagination = Boolean(pagination && totalItems > 0);

  const showEmptyCreate =
    !loading && !loadError && !hasActiveFilters && totalItems === 0;
  const showEmptyFilter =
    !loading && !loadError && hasActiveFilters && roles.length === 0;

  const defaultTitle = t("work_security_module_title", {
    defaultValue: "Company internal documents",
  });
  // Title states (owner decision, PR-2 spec §P2):
  //  - one accessible area, system: unchanged ("Rolandas must see nothing different").
  //  - one accessible area, custom: the area's own name.
  //  - two or more: the selected area's name, or the module's default title
  //    while in "all areas" mode (scope.areaId === "").
  const headerTitle =
    scope.areas.length === 1
      ? scope.areas[0].isSystem
        ? defaultTitle
        : scope.areas[0].name
      : scope.areaId && scope.selectedArea
        ? scope.selectedArea.name
        : defaultTitle;

  const SectionNav: React.ComponentType<TabsProps> = isPhone ? SectionPicker : Tabs;

  const areaWarningKey =
    !areaWarningDismissed && scope.error === "load_failed"
      ? "ws_areas_load_failed"
      : !areaWarningDismissed && scope.error === "invalid_area"
        ? "ws_area_invalid_reset"
        : null;

  return (
    <Page fullWidth title={headerTitle}>
      {(scope.showSwitcher || scope.canManageAreas) && (
        <div className={`mb-4 flex flex-wrap items-center gap-4 text-sm ${PHONE_PAGE_GUTTER}`}>
          {scope.showSwitcher && (
            <div className="flex items-center gap-1.5">
              <span className="text-zinc-500">
                {t("area_switcher_label", { defaultValue: "Sritis" })}:
              </span>
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-zinc-900 hover:bg-zinc-100 max-lg:min-h-11 dark:text-zinc-100 dark:hover:bg-zinc-800">
                  {scope.areaId && scope.selectedArea
                    ? scope.selectedArea.name
                    : t("all_areas", { defaultValue: "Visos sritys" })}
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom start"
                  modal={false}
                  className="z-50 mt-1 min-w-[14rem] origin-top-left rounded-md border border-gray-200 bg-white p-1 shadow-lg outline-none [--anchor-gap:4px] transition data-closed:scale-95 data-closed:opacity-0"
                >
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => scope.setAreaId("")}
                        className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm max-lg:min-h-11 ${
                          focus ? "bg-indigo-50" : ""
                        } ${!scope.areaId ? "font-semibold text-indigo-800" : "text-gray-800"}`}
                      >
                        {t("all_areas", { defaultValue: "Visos sritys" })}
                      </button>
                    )}
                  </MenuItem>
                  {scope.areas.map((area) => (
                    <MenuItem key={area.id}>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={() => scope.setAreaId(area.id)}
                          className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm max-lg:min-h-11 ${
                            focus ? "bg-indigo-50" : ""
                          } ${
                            scope.areaId === area.id
                              ? "font-semibold text-indigo-800"
                              : "text-gray-800"
                          }`}
                        >
                          {area.name}
                        </button>
                      )}
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>
            </div>
          )}
          {scope.canManageAreas && (
            <Link
              to={`/app/${companyId}/settings/procedures`}
              className="font-medium text-blue-600 hover:text-blue-800 max-lg:inline-flex max-lg:min-h-11 max-lg:items-center"
            >
              {t("manage_areas_link", { defaultValue: "Tvarkyti sritis" })} &rarr;
            </Link>
          )}
        </div>
      )}
      {areaWarningKey && (
        <div className="mb-4">
          <Banner tone="warning" onDismiss={() => setAreaWarningDismissed(true)}>
            {t(areaWarningKey, {
              defaultValue:
                areaWarningKey === "ws_areas_load_failed"
                  ? "Unable to load instruction spheres. Showing all accessible items."
                  : "The requested sphere is not available to you. Showing the default sphere.",
            })}
          </Banner>
        </div>
      )}
      <SectionNav
        tabs={visibleTabDescriptors.map((tab) => ({
          id: `ws-${tab.key}`,
          content: t(tab.labelKey, { defaultValue: tab.defaultLabel }),
          panelID: tab.panelId,
          ...(tab.badge ? { badge: String(tab.badge) } : {}),
        }))}
        selected={selectedTab}
        onSelect={handleTabSelect}
      >
        {activeTabKey === "roles" ? (
          <div className="space-y-4">
            <div
              className={`mt-4 flex flex-wrap justify-end gap-3 ${PHONE_PAGE_GUTTER}`}
            >
              <PolarisButton onClick={handleManageGroupsClick}>
                {t("manage_groups", { defaultValue: "Manage Groups" })}
              </PolarisButton>
              <PolarisButton variant="primary" onClick={handleCreateRoleClick}>
                {t("create_role", { defaultValue: "Create Role" })}
              </PolarisButton>
            </div>

            {loadError && roles.length > 0 && (
              <Banner tone="critical">
                {t("roles_load_failed", {
                  defaultValue: "Unable to refresh roles. Please try again.",
                })}
              </Banner>
            )}

            <Card>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-2 items-end">
                <div className="col-span-2">
                  <TextField
                    label={t("search_roles", { defaultValue: "Search Roles" })}
                    value={textSearch}
                    onChange={setTextSearch}
                    placeholder={t("search_roles_placeholder", {
                      defaultValue: "Search by role name or description...",
                    })}
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => setTextSearch("")}
                  />
                </div>
                <div>
                  <Select
                    label={t("filter_by_group", { defaultValue: "Filter by Group" })}
                    options={[
                      { label: t("all_groups", { defaultValue: "All Groups" }), value: "" },
                      {
                        label: t("without_group", { defaultValue: "Without Group" }),
                        value: "none",
                      },
                      ...groups.map((group) => ({
                        label: group.name,
                        value: group.id,
                      })),
                    ]}
                    value={groupFilter}
                    onChange={(value) => {
                      setGroupFilter(value);
                      setPaginationState((prev) => ({ ...prev, page: 1 }));
                    }}
                  />
                </div>
                <div>
                  <Select
                    label={t("filter_by_assignment", { defaultValue: "Filter by Assignment" })}
                    options={[
                      { label: t("all_roles", { defaultValue: "All Roles" }), value: "" },
                      {
                        label: t("roles_with_users", { defaultValue: "With Assigned Users" }),
                        value: "with_users",
                      },
                      {
                        label: t("roles_without_users", { defaultValue: "Without Assigned Users" }),
                        value: "without_users",
                      },
                    ]}
                    value={assignmentFilter}
                    onChange={(value) => {
                      setAssignmentFilter(value);
                      setPaginationState((prev) => ({ ...prev, page: 1 }));
                    }}
                  />
                </div>
              </div>
            </Card>

            <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
              {loading && roles.length === 0 ? (
                <DataTableSkeleton rows={8} columns={8} />
              ) : loadError && roles.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
                    {t("roles_load_failed_title", {
                      defaultValue: "Unable to Load Roles",
                    })}
                  </h3>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    {t("roles_load_failed_description", {
                      defaultValue: "We couldn't load roles. Please try again.",
                    })}
                  </p>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={loadRoles}
                      className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 cursor-pointer"
                    >
                      {t("retry", { defaultValue: "Retry" })}
                    </button>
                  </div>
                </div>
              ) : showEmptyFilter ? (
                <div className="text-center py-16 px-4">
                  <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
                    {t("no_roles_match_filters", { defaultValue: "No roles match your filters" })}
                  </h3>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    {t("no_roles_match_filters_description", {
                      defaultValue: "Try adjusting your search or filter criteria.",
                    })}
                  </p>
                </div>
              ) : showEmptyCreate ? (
                <div className="text-center py-16 px-4">
                  <svg
                    className="mx-auto h-12 w-12 text-zinc-400 dark:text-zinc-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
                    {t("no_roles", { defaultValue: "No Roles" })}
                  </h3>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    {t("no_roles_description", {
                      defaultValue:
                        "Create a role to start defining rules and signing requirements.",
                    })}
                  </p>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleCreateRoleClick}
                      className="relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 cursor-pointer"
                    >
                      {t("create_first_role", { defaultValue: "Create First Role" })}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <DataTable
                    collection={rolesCollection}
                    rowUrl={(item: any) => item.viewUrl}
                    columns={withoutMinorColumns([
                      { key: "#" },
                      {
                        key: "name",
                        name: t("role_name", { defaultValue: "Role Name" }),
                        mobileView: "topLeft",
                      },
                      {
                        key: "group",
                        name: t("group", { defaultValue: "Group" }),
                        type: "custom",
                        mobileView: "topRight",
                        render: (item: any) =>
                          item.group?.name ? (
                            <Badge color="purple">{item.group.name}</Badge>
                          ) : (
                            <span className="text-zinc-400 italic">
                              {t("no_group", { defaultValue: "No group" })}
                            </span>
                          ),
                      },
                      {
                        key: "description",
                        name: t("description", { defaultValue: "Description" }),
                        type: "custom",
                        mobileView: "bottomLeft",
                        render: (item: any) =>
                          item.description || (
                            <span className="text-zinc-400 italic">
                              {t("no_description", { defaultValue: "No description" })}
                            </span>
                          ),
                      },
                      // "Sritis" (PR-2 §P3) — only in "all spheres" mode; a
                      // concrete selection already scopes the whole table to
                      // one sphere, so the column would be pure noise there.
                      ...(scope.showSwitcher && !scope.selectedArea
                        ? [
                            {
                              key: "area",
                              name: t("area_switcher_label", { defaultValue: "Sritis" }),
                              type: "custom" as const,
                              render: (item: any) => {
                                const area = scope.areas.find((a) => a.id === item.area);
                                return (
                                  <Badge color="teal">
                                    {area
                                      ? area.name
                                      : t("area_unknown", { defaultValue: "Other sphere" })}
                                  </Badge>
                                );
                              },
                            },
                          ]
                        : []),
                      {
                        key: "documentsCount",
                        name: t("documents", { defaultValue: "Documents" }),
                        type: "custom",
                        render: (item: any) => (
                          <div className="text-center">
                            <Badge color="zinc">{item.documentsCount ?? 0}</Badge>
                          </div>
                        ),
                      },
                      {
                        key: "assignedUsers",
                        name: t("assigned_users", { defaultValue: "Assigned Users" }),
                        type: "custom",
                        render: (item: any) => (
                          <div className="text-center">
                            <Badge color="blue">{item.assignedUsersCount ?? 0}</Badge>
                          </div>
                        ),
                      },
                      {
                        key: "createdAt",
                        name: t("created_at", { defaultValue: "Created At" }),
                        type: "date",
                        mobileView: "bottomRight",
                      },
                      {
                        key: "actions",
                        name: "",
                        type: "custom",
                        render: (item: any) => (
                          <div className="text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(item.viewUrl);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-semibold bg-transparent border-0 cursor-pointer max-md:min-h-11"
                            >
                              {t("view", { defaultValue: "View" })} &rarr;
                            </button>
                          </div>
                        ),
                      },
                    ])}
                  />
                  {shouldShowPagination && (
                    <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
                      <PaginationControls
                        page={page}
                        pageSize={pageSize}
                        total={totalItems}
                        totalPages={totalPages}
                        hasNextPage={hasNextPage}
                        hasPreviousPage={hasPreviousPage}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={pageSizeOptions}
                        isLoading={loading}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : activeTabKey === "approvals" ? (
          <WsApprovalsTab
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        ) : activeTabKey === "incidents" ? (
          <WorkSecurityIncidentsPanel
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        ) : activeTabKey === "assignments" ? (
          <WorkSecurityAssignmentsPanel
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        ) : activeTabKey === "signed-documents" ? (
          <WorkSecuritySignedDocumentsPanel
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        ) : activeTabKey === "archive" ? (
          <WorkSecurityArchivedPanel
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        ) : activeTabKey === "positions" ? (
          <div className={`mt-4 ${PHONE_PAGE_GUTTER}`}>{companyId && <PositionsList companyId={companyId} />}</div>
        ) : (
          <WorkSecurityTrainingPanel
            areaId={scope.areaId}
            selectedArea={scope.selectedArea}
            areas={scope.areas}
          />
        )}
      </SectionNav>
    </Page>
  );
}
